import React, { useCallback, useState } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';

import { useUser } from '../../hooks/useUser';
import { getErrorMessage } from '../../helpers/getErrorMessage';
import { getS3ObjectKey } from '../../helpers/s3CopyPath';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../notifications';
import When from './When';

type CopyS3PathButtonProps = {
    path?: string | null;
    size?: 'sm' | 'md';
};

function CopyS3PathButton({ path, size = 'sm' }: CopyS3PathButtonProps) {
    const { isSuperAdmin } = useUser();
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            event.preventDefault();
            if (!path) {
                return;
            }
            try {
                await navigator.clipboard.writeText(getS3ObjectKey(path));
                setCopied(true);
                showSuccessNotification('S3 key copied');
                window.setTimeout(() => setCopied(false), 1500);
            } catch (error: unknown) {
                showErrorNotification(getErrorMessage(error));
            }
        },
        [path],
    );

    return (
        <When condition={isSuperAdmin && Boolean(path)}>
            <Tooltip
                label={copied ? 'Copied' : 'Copy S3 key'}
                withArrow
                openDelay={300}
            >
                <ActionIcon
                    variant="subtle"
                    size={size}
                    className="text-primary hover:bg-primary/10"
                    onClick={handleCopy}
                    aria-label="Copy S3 key"
                    title="Copy S3 key"
                >
                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </ActionIcon>
            </Tooltip>
        </When>
    );
}

export default CopyS3PathButton;
